import React, { useEffect, useRef, useState } from 'react';
import { Snackbar, Alert, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { onJobDone, JobNotice } from '../services/backgroundJobs';

/**
 * Pop-up for analyses that finish while the user is elsewhere (another page,
 * or another browser tab). Clicking "View" opens the result, which the job
 * store serves instantly. If the browser tab is in the background, its title
 * shows "✓ Ready" until the user comes back, and a system notification is
 * shown too when the browser already allows them (it never asks).
 */
export default function JobNotifier() {
  const navigate = useNavigate();
  const [queue, setQueue] = useState<JobNotice[]>([]);
  const baseTitle = useRef<string | null>(null);

  useEffect(() => onJobDone((n) => {
    setQueue((q) => [...q.filter((x) => x.key !== n.key), n]);
    if (document.hidden) {
      if (baseTitle.current === null) baseTitle.current = document.title;
      document.title = `✓ Ready · ${baseTitle.current}`;
      try {
        if ('Notification' in window && Notification.permission === 'granted') {
          const note = new Notification(n.ok ? `${n.label} is ready` : `${n.label} didn't finish`, { tag: n.key });
          note.onclick = () => { window.focus(); if (n.route) navigate(n.route); note.close(); };
        }
      } catch { /* notifications unavailable */ }
    }
  }), [navigate]);

  useEffect(() => {
    const onVisible = () => {
      if (!document.hidden && baseTitle.current !== null) {
        document.title = baseTitle.current;
        baseTitle.current = null;
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const current = queue[0];
  const close = () => setQueue((q) => q.slice(1));

  return (
    <Snackbar open={!!current} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      onClose={(_, reason) => { if (reason !== 'clickaway') close(); }}>
      {current ? (
        <Alert severity={current.ok ? 'success' : 'warning'} variant="filled" onClose={close} sx={{ alignItems: 'center' }}
          action={current.route ? (
            <Button color="inherit" size="small" onClick={() => { navigate(current.route!); close(); }} sx={{ fontWeight: 700 }}>
              View
            </Button>
          ) : undefined}>
          {current.ok ? `${current.label} is ready` : `${current.label} didn't finish`}
          {queue.length > 1 ? ` (+${queue.length - 1} more)` : ''}
        </Alert>
      ) : <span />}
    </Snackbar>
  );
}
